/**
 * StringOutputs virtual views: six de-prefixed, ancestry-enriched surfaces
 * fronting the ~80-column `StringOutputs_Network_Agent` source.
 *
 * The source carries network-matched ("nmv") per-string outputs as a single
 * wide row keyed by the `wellString` producing-string relation. Each view here
 * slices that wide row into one concern (pump, motor, gas-lift, solver match,
 * flow-assurance, string conditions), strips the `nmv` prefix to canonical
 * names via the shared shapes, and enriches each row with its well / field /
 * reservoir ancestry resolved through the FlatHierarchy registry.
 *
 * Built per session (not module-level) because the enriched build closes over
 * the CDF client + hierarchy/entity views to custom-fetch and resolve ancestry.
 */

import { describeAncestry, STRING_CONTEXT_ENRICH } from '../enrichAncestors.ts';
import type { Shape } from '../shapes/index.ts';
import {
  FLOW_ASSURANCE_FLAGS,
  GAS_LIFT_OUTPUTS,
  MOTOR_OUTPUTS,
  PHASE_RATES,
  PUMP_OUTPUTS,
  WELL_PRESSURES,
  WELL_SOLVER_MATCH,
} from '../shapes/index.ts';
import { makeStringContextViews } from './makeStringContextViews.ts';
import type { ViewFamilySpec } from './viewFamilySpec.ts';
import { StringOutputsNetworkAgentView } from '#schema/generated/ipso/views.ts';

/**
 * Well-bore operating pressures present on StringOutputs: bottom-hole, well-head
 * pressure/temperature, drawdown. The base `WELL_PRESSURES` leads with
 * `fbhp`/`fwhp` (source `nmvFBHP`/`nmvFWHP`), which this source does NOT expose —
 * its solver-matched flowing pressures live on `WELL_SOLVER_MATCH` instead — so
 * the leading two members are dropped to avoid resolving to nonexistent columns.
 */
const STRING_CONDITION_PRESSURES: Shape = WELL_PRESSURES.slice(2);

/** Whole-string condition extras alongside the phase rates and pressures (GOR/GLR/cuts/frequency/reservoir pressure). */
const STRING_CONDITION_EXTRAS: Shape = [
  { canonical: 'gor', suffix: 'Gor', description: 'Gas-oil ratio.', kind: 'gasOilRatio' },
  { canonical: 'glr', suffix: 'Glr', description: 'Gas-liquid ratio.', kind: 'gasOilRatio' },
  { canonical: 'wgr', suffix: 'Wgr', description: 'Water-gas ratio.', kind: 'liquidGasRatio' },
  { canonical: 'cgr', suffix: 'Cgr', description: 'Condensate-gas ratio.', kind: 'liquidGasRatio' },
  { canonical: 'waterCut', suffix: 'WaterCut', description: 'Water cut.', kind: 'fraction' },
  { canonical: 'frequency', suffix: 'Frequency', description: 'Drive (VSD) operating frequency.', kind: 'frequency' },
  {
    canonical: 'reservoirPressure',
    suffix: 'ReservoirPressure',
    description: 'Reservoir pressure.',
    kind: 'pressureAbsolute',
  },
];

/** Mixture flow velocities relative to the erosional limit, raised alongside the flow-assurance flags. */
const FLOW_VELOCITIES: Shape = [
  {
    canonical: 'erosionalVelocity',
    suffix: 'ErosionalVelocity',
    description: 'Erosional velocity limit for the string.',
    kind: 'velocity',
  },
  {
    canonical: 'mixtureVelocity',
    suffix: 'MixtureVelocity',
    description: 'Produced-mixture flow velocity.',
    kind: 'velocity',
  },
];

/** StringContext: well/field/reservoir ancestry off the `wellString` ref. */

/** One concern-scoped StringOutputs surface: a canonical name, agent doc, and the de-prefixed groups to keep. */
type StringOutputsSpec = ViewFamilySpec;

/**
 * The six StringOutputs subviews, declared as data. Each de-prefixes the `nmv`
 * measure family for one concern; all share identity + the wellString relation
 * and the well/field/reservoir ancestry enrichment.
 */
const STRING_OUTPUTS_SPECS: readonly StringOutputsSpec[] = [
  {
    name: 'StringPumpOutputs',
    description: `Per-string ESP/pump operating outputs (efficiency, head, intake/outlet pressure, power, rate)${describeAncestry(STRING_CONTEXT_ENRICH.relations)}`,
    groups: [{ shape: PUMP_OUTPUTS, prefix: 'nmv' }],
  },
  {
    name: 'StringMotorOutputs',
    description: `Per-string electrical motor/drive outputs (current, efficiency, load, power, RPM, surface kVA)${describeAncestry(STRING_CONTEXT_ENRICH.relations)}`,
    groups: [{ shape: MOTOR_OUTPUTS, prefix: 'nmv' }],
  },
  {
    name: 'StringGasLiftOutputs',
    description: `Per-string gas-lift injection design and operating outputs (CHP, injection depth, orifice, valve pressures, GLR/injected GLR)${describeAncestry(STRING_CONTEXT_ENRICH.relations)}`,
    groups: [{ shape: GAS_LIFT_OUTPUTS, prefix: 'nmv' }],
  },
  {
    name: 'StringWellSolverMatch',
    description: `Per-string well-solver match diagnostics: measured/estimated/difference per phase plus solver-matched pressures, GOR and water cut${describeAncestry(STRING_CONTEXT_ENRICH.relations)}`,
    groups: [{ shape: WELL_SOLVER_MATCH, prefix: 'nmv' }],
  },
  {
    name: 'StringFlowAssuranceFlags',
    description: `Per-string flow-assurance condition flags (asphaltene, hydrate, scale, wax, PE5 stability) plus mixture/erosional velocities${describeAncestry(STRING_CONTEXT_ENRICH.relations)}`,
    groups: [
      { shape: FLOW_ASSURANCE_FLAGS, prefix: 'nmv' },
      { shape: FLOW_VELOCITIES, prefix: 'nmv' },
    ],
  },
  {
    name: 'StringConditions',
    description: `Per-string matched operating conditions: well-level phase rates, bottom-hole/well-head pressure & temperature, drawdown, GOR/GLR/WGR/CGR, water cut, drive frequency and reservoir pressure${describeAncestry(STRING_CONTEXT_ENRICH.relations)}`,
    groups: [
      { shape: PHASE_RATES, prefix: 'nmvWell' },
      { shape: STRING_CONDITION_PRESSURES, prefix: 'nmv' },
      { shape: STRING_CONDITION_EXTRAS, prefix: 'nmv' },
    ],
  },
];

/**
 * Build the six de-prefixed, ancestry-enriched StringOutputs surfaces, each a
 * StringContext view over the shared source + well/field/reservoir enrichment;
 * filter by wellString (or join WellEntity) to scope by hierarchy.
 */
export const useStringOutputsViews = makeStringContextViews({
  source: () => StringOutputsNetworkAgentView,
  specs: STRING_OUTPUTS_SPECS,
});
